import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import OrderSummary from "./components/OrderSummary";
import OrderInvoice from "./components/OrderInvoice";
import { customerOrdersApi } from "../api/api";
import toast from 'react-hot-toast';
import { DisplayContext, formatOrderId } from "../utils/orderFormatter";
import { useTheme } from "../context/ThemeContext";

export default function CustomerOrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState("summary");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch order data based on orderId
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        console.log('🔍 CustomerOrderDetailsPage - Fetching order:', orderId);
        
        // Fetch the specific order from the API
        const response = await customerOrdersApi.getOrders({ page: 0, size: 100 });
        const allOrders = response.data.content || [];
        
        console.log('🔍 CustomerOrderDetailsPage - All orders:', allOrders);
        
        // Find the order with matching ID
        const foundOrder = allOrders.find((o: any) => o.id === parseInt(orderId || '0'));
        
        console.log('🔍 CustomerOrderDetailsPage - Found order:', foundOrder);
        console.log('🔍 CustomerOrderDetailsPage - Order items:', foundOrder.items);
        
        if (foundOrder) {
          // Transform the order data to match the expected format
          const transformedOrder = {
            id: foundOrder.id,
            customOrderId: foundOrder.customOrderId || `D-${foundOrder.id}`,
            date: new Date(foundOrder.createdAt).toLocaleString(),
            status: foundOrder.status,
            total: foundOrder.total || foundOrder.items?.reduce((sum: number, item: any) =>
              sum + (item.quantity * item.price), 0) || 0,
            vendor: foundOrder.vendorName || 'Unknown Vendor',
            // Table info
            tableName: foundOrder.tableName,
            floorName: foundOrder.floorName,
            sectionName: foundOrder.sectionName,
            // Tax breakdown fields
            subtotal: foundOrder.subtotal,
            cgstRate: foundOrder.cgstRate,
            cgstAmount: foundOrder.cgstAmount,
            sgstRate: foundOrder.sgstRate,
            sgstAmount: foundOrder.sgstAmount,
            igstRate: foundOrder.igstRate,
            igstAmount: foundOrder.igstAmount,
            serviceChargeRate: foundOrder.serviceChargeRate,
            serviceChargeAmount: foundOrder.serviceChargeAmount,
            discountAmount: foundOrder.discountAmount,
            taxType: foundOrder.taxType,
            items: foundOrder.items?.map((item: any) => {
              console.log('🔍 CustomerOrderDetailsPage - Mapping item:', item);
              const itemName = item.itemName || item.menuItem?.name || item.menuItemName || item.name || 'Unknown Item';
              console.log('🔍 CustomerOrderDetailsPage - Item name extracted:', itemName);
              return {
                name: itemName,
                qty: item.quantity || 0,
                price: item.price || 0,
                comment: item.comment,
                toppings: item.toppings,
                requestedSpiceLevel: item.requestedSpiceLevel,
                portionName: item.portionName,
                imageUrl: item.imageUrl || item.menuItem?.imageUrl
              };
            }) || []
          };
          
          console.log('🔍 CustomerOrderDetailsPage - Transformed order:', transformedOrder);
          
          setOrderData(transformedOrder);
        } else {
          toast.error('Order not found');
          setOrderData(null);
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
        toast.error('Failed to load order details');
        setOrderData(null);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center">Order not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm transition-colors"
          style={{ color: theme.gray[500] }}
        >
          ← Back to Orders
        </button>
        <h2 className="text-xl font-semibold" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Order Details</h2>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6" style={{ borderColor: theme.components.card.border }}>
        {[
          { key: "summary", label: "Order Summary" },
          { key: "invoice", label: "Invoice" }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-2 font-medium transition"
            style={{
              borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
              color: activeTab === tab.key ? theme.text : theme.gray[500],
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "summary" ? (
        <OrderSummary order={orderData} />
      ) : (
        <OrderInvoice order={orderData} />
      )}
    </div>
  );
}
